import { config } from "../config";
import { callGateway, type CallerIdentity } from "./gateway";
import { isParentScoped, rulePrices, rupeeGross, type PriceComponent, type PriceRule } from "./priceList";

/**
 * Festive promotions (owner 2026-10-14, radius-wiki ankhos-plan-builder-design.md
 * §9.17): a dated PROMOTIONAL PRICE on chosen plans. For the festival dates the
 * plan costs less, then its normal price returns by itself. radbilling writes it
 * as price rules in one transaction; everyone who pays in the window gets it.
 *
 *   POST /promotion/{ns}                 radbilling POST /priceruleapi/{ns}/promotions
 *   PUT  /promotion/{ns}/{slug}/cancel   radbilling PUT  /priceruleapi/{ns}/promotions/{slug}/cancel
 *
 * Grant: promotion:write -- superadmin, bssadmin, Operator; never Support
 * (radaccess v1.97).
 */

export const PROMOTION_REF = "promotion/";

export interface PromotionRequest {
  name: string;
  /** YYYY-MM-DD; the festive price starts 00:00 UTC (05:30 IST) that day. */
  from: string;
  /** YYYY-MM-DD; the normal price returns 00:00 UTC that day. */
  to: string;
  percent?: number;
  rupeesoff?: number;
  componentids: string[];
  /** Caps a percentage in rupees per component ("10% off, up to ₹500"). */
  maxoff?: number;
  /** An item whose normal price is below this takes no festive price (§9.19). */
  minbill?: number;
  preview?: boolean;
}

/** The two fields the promotion screens show of a component. */
export type PriceComponentLike = Pick<PriceComponent, "id" | "name">;

export interface PromotionPlan {
  componentid: string;
  name: string;
  normalgross: number;
  festivegross: number;
}

export interface PromotionResult {
  slug: string;
  from: string;
  to: string;
  plans: PromotionPlan[];
  preview: boolean;
}

const enc = encodeURIComponent;

export async function createPromotion(identity: CallerIdentity, ns: string, req: PromotionRequest): Promise<PromotionResult> {
  const r = await callGateway<PromotionResult>(identity, config().gatewayBase, `/promotion/${enc(ns)}`, {
    method: "POST",
    body: req,
  });
  return r.data;
}

export async function cancelPromotion(identity: CallerIdentity, ns: string, slug: string): Promise<{ slug: string; plans: number }> {
  const r = await callGateway<{ slug: string; plans: number }>(
    identity,
    config().gatewayBase,
    `/promotion/${enc(ns)}/${enc(slug)}/cancel`,
    { method: "PUT" },
  );
  return r.data;
}

/**
 * Anything on sale may take a festive price -- plans, add-ons, one-time fees
 * (owner 2026-10-14, radbilling v0.1.91). Only a one-time fee may come to ₹0.
 */
export function isPromotable(c: Pick<PriceComponent, "active">): boolean {
  return c.active;
}

/** A plan, as promotion codes see it (a code is typed at a plan checkout). Mirrors radbilling pricesAPlan. */
export function isPlanComponent(c: Pick<PriceComponent, "radcatalog_groupuid" | "labels" | "active">): boolean {
  if (!c.active) return false;
  if (c.radcatalog_groupuid) return true;
  return Boolean(c.labels && "product" in c.labels && !c.labels.kind);
}

export type PromotionState = "scheduled" | "running" | "ended" | "cancelled";

export interface PromotionSummary {
  slug: string;
  from: string;
  to: string;
  plans: number;
  state: PromotionState;
}

const dateOf = (s: string) => s.slice(0, 10);

/**
 * The promotions in a namespace, read from its price rules: a festive rule's
 * catalog_ref is promotion/<slug>. A promotion cancelled before it started has
 * every festive rule withdrawn; one cancelled while running simply ends early.
 */
export function promotionsOf(rules: PriceRule[], now: Date): PromotionSummary[] {
  const bySlug = new Map<string, PriceRule[]>();
  for (const r of rules) {
    if (!r.catalogref?.startsWith(PROMOTION_REF)) continue;
    const slug = r.catalogref.slice(PROMOTION_REF.length);
    bySlug.set(slug, [...(bySlug.get(slug) ?? []), r]);
  }
  const today = now.toISOString().slice(0, 10);
  const out: PromotionSummary[] = [];
  for (const [slug, rs] of bySlug) {
    const live = rs.filter((r) => r.active);
    const shown = live.length ? live : rs;
    const from = shown.map((r) => dateOf(r.effectivefrom)).sort()[0]!;
    const to = shown.map((r) => dateOf(r.effectiveto ?? "")).sort().reverse()[0]!;
    const state: PromotionState = !live.length
      ? "cancelled"
      : today < from
        ? "scheduled"
        : today < to
          ? "running"
          : "ended";
    out.push({ slug, from, to, plans: shown.length, state });
  }
  return out.sort((a, b) => b.from.localeCompare(a.from));
}

/**
 * One plan in a promotion, as the operator quotes it (owner 2026-10-14: "If
 * somebody asks what discount you offer for Diwali 2026 then I can open and
 * give the exact price of a particular plan"). Read from the price rules: the
 * festive rule gives the festive price; the normal price is the one in force
 * when the promotion ends (the rule radbilling resumes it with).
 */
export interface PromotionPlanDetail {
  componentid: string;
  name: string;
  normal: number;
  festive: number;
}

export interface PromotionDetail {
  plans: PromotionPlanDetail[];
  /** "10% off", "₹50 off", or "" when the plans differ in a way neither explains. */
  offer: string;
}

export function promotionDetail(slug: string, rules: PriceRule[], components: PriceComponent[]): PromotionDetail {
  const mine = rules.filter((r) => r.catalogref === PROMOTION_REF + slug);
  const shown = mine.some((r) => r.active) ? mine.filter((r) => r.active) : mine;
  const normalRules = rules.filter((r) => !r.catalogref?.startsWith(PROMOTION_REF));
  const plans: PromotionPlanDetail[] = [];
  for (const r of shown) {
    for (const c of components.filter((x) => rulePrices(r, x))) {
      const end = new Date(r.effectiveto ?? r.effectivefrom);
      plans.push({
        componentid: c.id,
        name: c.name,
        normal: planPriceAt(c, normalRules, end).gross,
        festive: r.pricingmode === "FREE" ? 0 : rupeeGross(r.amount, c.gstpercent),
      });
    }
  }
  plans.sort((a, b) => a.name.localeCompare(b.name));
  return { plans, offer: offerOf(plans) };
}

/**
 * The discount the operator typed, recovered from the prices: a percentage p
 * reproduces every plan's rupees off as round(normal * p / 100), the way
 * radbilling computed it; otherwise the same rupees off on every plan.
 */
export function offerOf(plans: Pick<PromotionPlanDetail, "normal" | "festive">[]): string {
  if (plans.length === 0) return "";
  const offs = plans.map((p) => p.normal - p.festive);
  if (plans.every((p, i) => p.normal > 0 && offs[i]! >= 0)) {
    for (let half = 1; half < 200; half++) {
      const pct = half / 2;
      if (plans.every((p, i) => Math.round((p.normal * pct) / 100) === offs[i])) return `${pct}% off`;
    }
  }
  return offs.every((o) => o === offs[0]) ? `₹${offs[0]} off` : "";
}

/**
 * What a plan costs the subscriber at `at`, in whole rupees incl. GST -- the
 * figure radbilling grossOf rounds to and every promotion is computed from.
 * Owner 2026-10-14: "If the cost of component is 150, then there is no point
 * to provide 100 off ... User should know the price also next to plan details."
 * The rule in force wins (a rule by id over a selector, then the newest);
 * without one, the component's own price. `festive` marks a promotional price
 * running now -- a code cannot be added to it.
 */
export interface PlanPrice {
  gross: number;
  festive: boolean;
  /** A ONE_TIME fee (activation, installation): the only kind that may be free. */
  oneTime: boolean;
}

export function planPriceAt(c: PriceComponent, rules: PriceRule[], at: Date): PlanPrice {
  const when = at.toISOString();
  const inForce = rules
    .filter((r) => r.active && !isParentScoped(r) && rulePrices(r, c))
    .filter((r) => r.effectivefrom <= when && (!r.effectiveto || r.effectiveto > when))
    .sort((a, b) => Number(Boolean(b.componentid)) - Number(Boolean(a.componentid)) || b.effectivefrom.localeCompare(a.effectivefrom));
  const r = inForce[0];
  const net = !r ? c.price : r.pricingmode === "FREE" ? 0 : r.pricingmode === "ABSOLUTE" ? r.amount : c.price;
  return {
    gross: rupeeGross(net, c.gstpercent),
    festive: Boolean(r?.catalogref?.startsWith(PROMOTION_REF)),
    oneTime: c.billingtype === "ONE_TIME",
  };
}

/**
 * The price after `value` off, the way radbilling rounds it: a percentage of
 * the gross to the rupee. `tooMuch` when the discount reaches the whole price
 * -- a festive promotion is refused then, a code stops at ₹1.
 */
export function priceAfter(
  gross: number,
  kind: "percent" | "rupees",
  value: number,
  opts: { maxOff?: number | undefined; mayBeFree?: boolean } = {},
): { after: number; tooMuch: boolean } {
  let off = kind === "percent" ? Math.round((gross * value) / 100) : value;
  if (kind === "percent" && opts.maxOff && off > opts.maxOff) off = opts.maxOff;
  if (opts.mayBeFree) return { after: Math.max(gross - off, 0), tooMuch: false };
  return { after: Math.max(gross - off, 1), tooMuch: off >= gross };
}

/** "diwali-2026" -> "Diwali 2026"; a reused name ("diwali-2026--2") reads the same. */
export function promotionTitle(slug: string): string {
  const words = slug.replace(/--\d+$/, "").replace(/-/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The last day the festive price is paid: the day before `to`. */
export function lastDay(to: string): string {
  const d = new Date(`${to}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

/**
 * Promotion codes (owner 2026-10-14, design §9.18): a subscriber types a code
 * at online checkout and pays less -- once per subscriber, optional total cap,
 * never together with a festive price.
 *
 *   POST /promocode/{ns}                    radbilling POST /promocodeapi/{ns}
 *   GET  /promocode/{ns}                    radbilling GET  /promocodeapi/{ns}
 *   PUT  /promocode/{ns}/{code}/deactivate  radbilling PUT  /promocodeapi/{ns}/{code}/deactivate
 *
 * Grant: promocode:read/write -- superadmin, bssadmin, Operator (radaccess v1.98).
 */
export interface PromoCode {
  id: string;
  code: string;
  percent?: number;
  rupeesoff?: number;
  validfrom: string;
  validto: string;
  componentids?: string[];
  maxuses?: number;
  maxoff?: number;
  minbill?: number;
  /** §9.19: who may use it ("" / absent = everyone), extra days instead of money off, conditions. */
  audience?: PromoAudience;
  bonusdays?: number;
  mintermdays?: number;
  mintenuredays?: number;
  /** Owner 2026-10-15: payments already made in a row for a plan of the same length. */
  minpaidcycles?: number;
  active: boolean;
  uses: number;
  held: number;
}

export interface NewPromoCode {
  code: string;
  percent?: number;
  rupeesoff?: number;
  from: string;
  to: string;
  componentids?: string[];
  maxuses?: number;
  maxoff?: number;
  minbill?: number;
  audience?: PromoAudience;
  bonusdays?: number;
  mintermdays?: number;
  mintenuredays?: number;
  /** Owner 2026-10-15: payments already made in a row for a plan of the same length. */
  minpaidcycles?: number;
}

export type PromoAudience = "new" | "existing";

/** One use (or live hold) of a code -- radbilling GET /promocodeapi/{ns}/{code}/uses (§9.19). */
export interface PromoUse {
  id: string;
  accountid: string;
  componentid: string;
  plan: string;
  /** USED once paid; RESERVED while a checkout waits for payment. */
  status: "USED" | "RESERVED";
  normalgross: number;
  rupeesoff: number;
  bonusdays: number;
  orderref: string;
  at: string;
  expiresat: string;
}

export async function listPromoUses(identity: CallerIdentity, ns: string, code: string): Promise<PromoUse[]> {
  const r = await callGateway<PromoUse[] | null>(identity, config().gatewayBase, `/promocode/${enc(ns)}/${enc(code)}/uses`);
  return r.data ?? [];
}

/** The report's totals: paid uses only; a hold that was never paid gave nothing away. */
export function promoUseTotals(uses: PromoUse[], now: Date): { used: number; held: number; rupeesOff: number; bonusDays: number } {
  const used = uses.filter((u) => u.status === "USED");
  return {
    used: used.length,
    held: uses.filter((u) => u.status === "RESERVED" && new Date(u.expiresat) > now).length,
    rupeesOff: used.reduce((n, u) => n + u.rupeesoff, 0),
    bonusDays: used.reduce((n, u) => n + u.bonusdays, 0),
  };
}

export async function listPromoCodes(identity: CallerIdentity, ns: string): Promise<PromoCode[]> {
  const r = await callGateway<PromoCode[] | null>(identity, config().gatewayBase, `/promocode/${enc(ns)}`);
  return r.data ?? [];
}

export async function createPromoCode(identity: CallerIdentity, ns: string, req: NewPromoCode): Promise<PromoCode> {
  const r = await callGateway<PromoCode>(identity, config().gatewayBase, `/promocode/${enc(ns)}`, { method: "POST", body: req });
  return r.data;
}

export async function deactivatePromoCode(identity: CallerIdentity, ns: string, code: string): Promise<void> {
  await callGateway(identity, config().gatewayBase, `/promocode/${enc(ns)}/${enc(code)}/deactivate`, { method: "PUT" });
}

export type PromoCodeState = "scheduled" | "active" | "expired" | "used up" | "deactivated";

/** Where a code stands, in words. */
export function promoCodeState(p: PromoCode, now: Date): PromoCodeState {
  if (!p.active) return "deactivated";
  const today = now.toISOString().slice(0, 10);
  if (today < p.validfrom.slice(0, 10)) return "scheduled";
  if (today >= p.validto.slice(0, 10)) return "expired";
  if (p.maxuses !== undefined && p.uses >= p.maxuses) return "used up";
  return "active";
}

/**
 * "₹100 off", "10% off up to ₹500", "15 extra days", then the conditions:
 * "… on bills of ₹1000 or more", "… on 180-day plans only", "… for new
 * subscribers", "… for subscribers paying without a break for 180+ days".
 */
export function promoOff(
  p: Pick<PromoCode, "percent" | "rupeesoff" | "maxoff" | "minbill" | "audience" | "bonusdays" | "mintermdays" | "mintenuredays" | "minpaidcycles">,
): string {
  const off =
    p.bonusdays !== undefined
      ? `${p.bonusdays} extra days`
      : p.percent !== undefined
        ? `${p.percent}% off${p.maxoff ? ` up to ₹${p.maxoff}` : ""}`
        : `₹${p.rupeesoff ?? 0} off`;
  const parts = [off];
  if (p.minbill) parts.push(`on bills of ₹${p.minbill} or more`);
  if (p.mintermdays) parts.push(`on ${p.mintermdays}-day plans only`);
  if (p.audience === "new") parts.push("for new subscribers");
  if (p.audience === "existing") parts.push("for existing subscribers");
  if (p.mintenuredays) parts.push(`for subscribers paying without a break for ${p.mintenuredays}+ days`);
  if (p.minpaidcycles) parts.push(`after ${p.minpaidcycles} payments in a row for the same plan length`);
  return parts.join(" ");
}

/** A plan's term in days from its labels (term_days); 0 when unknown. Mirrors radbilling termDaysOf. */
export function termDaysOf(c: Pick<PriceComponent, "labels">): number {
  const n = Number(c.labels?.term_days ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/**
 * One sale made at a festive price -- radbilling GET /promotionuseapi/{ns},
 * published as /pricelist/{ns}/promotionuses (owner 2026-10-15: "build report
 * for all promotion used by which user"). Festive prices are not written per
 * sale: a sale counts when its plan was paid inside the promotion's dates.
 * Plans only; `paid` is the whole receipt (plan and any add-ons).
 */
export interface PromotionUse {
  promotion: string;
  componentid: string;
  plan: string;
  username: string;
  accountid: string;
  name: string;
  normalgross: number;
  festivegross: number;
  saved: number;
  paid: number;
  method: string;
  at: string;
}

export async function listPromotionUses(identity: CallerIdentity, ns: string): Promise<PromotionUse[]> {
  const r = await callGateway<PromotionUse[] | null>(identity, config().gatewayBase, `/pricelist/${enc(ns)}/promotionuses`);
  return r.data ?? [];
}

/** Per festive promotion: how many sales, and the rupees subscribers saved. */
export function festiveTotals(uses: Pick<PromotionUse, "promotion" | "saved">[]): { promotion: string; sales: number; saved: number }[] {
  const by = new Map<string, { promotion: string; sales: number; saved: number }>();
  for (const u of uses) {
    const t = by.get(u.promotion) ?? { promotion: u.promotion, sales: 0, saved: 0 };
    t.sales += 1;
    t.saved += u.saved;
    by.set(u.promotion, t);
  }
  return [...by.values()].sort((a, b) => a.promotion.localeCompare(b.promotion));
}
